import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MessageCircle, BadgeCheck, ShieldCheck, ArrowRight, Users, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const BAND_ACCENT = { "High Trust": "text-bloq-mint", Trusted: "text-bloq-mint", Established: "text-bloq-blue", Watch: "text-amber-400", New: "text-bloq-muted" };

// Shown in the Chats list when the inbox is empty — surfaces top-trust people + verified bloqs
// so a brand-new user instantly has somewhere to go (Lane D cold-start / viral loop).
export function ColdStartDiscover({ onStartChat }) {
  const navigate = useNavigate();
  const { data: peopleD } = useQuery({ queryKey: ["cold-people"], queryFn: () => api.discoverPeople("top-trust"), retry: false, staleTime: 5 * 60 * 1000 });
  const { data: bloqsD } = useQuery({ queryKey: ["cold-bloqs"], queryFn: () => api.discoverBloqs("verified"), retry: false, staleTime: 5 * 60 * 1000 });
  const people = (peopleD?.items || []).filter((p) => !p.is_me).slice(0, 5);
  const bloqs = (bloqsD?.items || []).slice(0, 4);

  return (
    <div className="px-3 py-4" data-testid="cold-start-discover">
      <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4 text-center">
        <MessageCircle className="mx-auto mb-2 h-9 w-9 text-bloq-muted/60" />
        <p className="font-semibold">Your inbox is quiet — for now</p>
        <p className="mt-0.5 text-xs text-bloq-muted">Start a chat, or say hi to some of the most-trusted people on BLOQ.</p>
        <button onClick={onStartChat} className="mt-3 inline-flex items-center gap-1.5 rounded-full bloq-gradient px-4 py-2 text-sm font-bold text-bloq-bg hover:opacity-90" data-testid="cold-start-new-chat">
          Start a chat
        </button>
      </div>

      {people.length > 0 && (
        <div className="mt-5">
          <span className="mb-2 block px-1 text-xs font-bold uppercase tracking-wider text-bloq-muted">People worth knowing</span>
          <div className="space-y-1.5">
            {people.map((p) => (
              <button key={p.handle} onClick={() => navigate(`/u/${p.handle}`)} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-2.5 text-left transition-colors hover:border-bloq-mint/40 hover:bg-white/[0.03]" data-testid={`cold-person-${p.handle}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-bloq-mint/70 to-bloq-blue/70 font-heading text-base font-bold text-bloq-bg">{(p.display_name || p.handle || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1"><span className="truncate text-sm font-semibold">{p.display_name || p.handle}</span>{p.verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-bloq-mint" />}</div>
                  <div className={cn("flex items-center gap-1 text-[11px]", BAND_ACCENT[p.band] || "text-bloq-muted")}><ShieldCheck className="h-3 w-3" /> {p.score ?? "—"} · {p.band || "—"}</div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-bloq-muted" />
              </button>
            ))}
          </div>
        </div>
      )}

      {bloqs.length > 0 && (
        <div className="mt-5">
          <span className="mb-2 block px-1 text-xs font-bold uppercase tracking-wider text-bloq-muted">Verified bloqs to join</span>
          <div className="space-y-1.5">
            {bloqs.map((c) => (
              <button key={c.id} onClick={() => navigate(`/app/bloqs/${c.id}`)} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-2.5 text-left transition-colors hover:border-bloq-mint/40 hover:bg-white/[0.03]" data-testid={`cold-bloq-${c.id}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-bloq-s2 text-lg">{c.emoji || "🟢"}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1"><span className="truncate text-sm font-semibold">{c.name}</span>{c.verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-bloq-mint" />}</div>
                  <div className="flex items-center gap-1 text-[11px] text-bloq-muted"><Users className="h-3 w-3" /> {c.member_count} member{c.member_count === 1 ? "" : "s"}</div>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-bloq-muted" />
              </button>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => navigate("/app/discover")} className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-full border border-white/12 py-2.5 text-sm font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/10" data-testid="cold-browse-all">
        <Compass className="h-4 w-4" /> Explore Discover
      </button>
    </div>
  );
}
